// Super Admin Portal API Slice - RBAC Management, Audit, System Administration
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl, getAuthBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();
const AUTH_BASE_URL = getAuthBaseUrl();

// Custom base query that routes to the correct base URL
const customBaseQuery = async (args, api, extraOptions) => {
  // Determine which base URL to use based on the endpoint
  const url = typeof args === 'string' ? args : args.url;
  const isSystemEndpoint =
    url?.startsWith('/system/') ||
    url?.startsWith('system/') ||
    url?.startsWith('/api/system/') ||
    url?.startsWith('api/system/');
  const isSecurityEndpoint = url?.startsWith('/security/') || url?.startsWith('security/');

  // Use API base URL for system and security endpoints, AUTH base URL for everything else
  const baseUrl = isSystemEndpoint || isSecurityEndpoint ? BASE_URL : AUTH_BASE_URL;

  // Call the base query with the appropriate base URL
  return baseQueryWithReauth(baseUrl)(args, api, extraOptions);
};

export const superAdminApiSlice = createApi({
  reducerPath: 'superAdminApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'User',
    'UserGroup',
    'Role',
    'Permission',
    'Organization',
    'Department',
    'AuditLog',
    'SystemHealth',
    'SecurityEvent',
    'UserSession',
    'SystemSettings',
  ],
  endpoints: (builder) => ({
    // ========== RBAC USER MANAGEMENT ==========
    fetchAllUsers: builder.query({
      query: (params = {}) => ({
        url: 'rbac/users/',
        params: {
          include_inactive: true,
          include_profiles: true,
          ...params,
        },
      }),
      providesTags: ['User'],
    }),

    createSystemUser: builder.mutation({
      query: (userData) => ({
        url: 'rbac/users/',
        method: 'POST',
        body: userData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['User'],
    }),

    updateSystemUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `rbac/users/${userId}/`,
        method: 'PUT',
        body: userData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['User'],
    }),

    deleteSystemUser: builder.mutation({
      query: (userId) => ({
        url: `rbac/users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    activateUser: builder.mutation({
      query: (userId) => ({
        url: `rbac/users/${userId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),

    deactivateUser: builder.mutation({
      query: (userId) => ({
        url: `rbac/users/${userId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),

    resetUserPassword: builder.mutation({
      query: ({ userId, newPassword }) => ({
        url: `rbac/users/${userId}/reset-password/`,
        method: 'POST',
        body: { password: newPassword },
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    // ========== RBAC GROUP MANAGEMENT ==========
    fetchAllGroups: builder.query({
      query: () => 'groups/',
      providesTags: ['UserGroup'],
    }),

    createSystemGroup: builder.mutation({
      query: (groupData) => ({
        url: 'groups/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UserGroup'],
    }),

    updateSystemGroup: builder.mutation({
      query: ({ groupId, ...groupData }) => ({
        url: `groups/${groupId}/`,
        method: 'PUT',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UserGroup'],
    }),

    deleteSystemGroup: builder.mutation({
      query: (groupId) => ({
        url: `groups/${groupId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserGroup'],
    }),

    assignUserToGroup: builder.mutation({
      query: ({ userId, groupId }) => ({
        url: `users/${userId}/assign-group/`,
        method: 'POST',
        body: { group_id: groupId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['User', 'UserGroup'],
    }),

    removeUserFromGroup: builder.mutation({
      query: ({ userId, groupId }) => ({
        url: `users/${userId}/remove-group/`,
        method: 'POST',
        body: { group_id: groupId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['User', 'UserGroup'],
    }),

    // ========== RBAC ROLES & PERMISSIONS ==========
    fetchAllRoles: builder.query({
      query: () => 'rbac/roles/',
      providesTags: ['Role'],
    }),

    createRole: builder.mutation({
      query: (roleData) => ({
        url: 'rbac/roles/',
        method: 'POST',
        body: roleData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Role'],
    }),

    updateRole: builder.mutation({
      query: ({ roleId, ...roleData }) => ({
        url: `rbac/roles/${roleId}/`,
        method: 'PUT',
        body: roleData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Role'],
    }),

    deleteRole: builder.mutation({
      query: (roleId) => ({
        url: `rbac/roles/${roleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),

    fetchAllPermissions: builder.query({
      query: () => 'rbac/permissions/',
      providesTags: ['Permission'],
    }),

    assignPermissionToRole: builder.mutation({
      query: ({ roleId, permissionId }) => ({
        url: `rbac/roles/${roleId}/assign-permission/`,
        method: 'POST',
        body: { permission_id: permissionId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Role', 'Permission'],
    }),

    removePermissionFromRole: builder.mutation({
      query: ({ roleId, permissionId }) => ({
        url: `rbac/roles/${roleId}/remove-permission/`,
        method: 'POST',
        body: { permission_id: permissionId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Role', 'Permission'],
    }),

    // ========== ORGANIZATION & DEPARTMENT MANAGEMENT ==========
    fetchOrganizations: builder.query({
      query: () => 'rbac/organizations/',
      providesTags: ['Organization'],
    }),

    createOrganization: builder.mutation({
      query: (orgData) => ({
        url: 'rbac/organizations/',
        method: 'POST',
        body: orgData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Organization'],
    }),

    updateOrganization: builder.mutation({
      query: ({ orgId, ...orgData }) => ({
        url: `rbac/organizations/${orgId}/`,
        method: 'PUT',
        body: orgData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Organization'],
    }),

    deleteOrganization: builder.mutation({
      query: (orgId) => ({
        url: `rbac/organizations/${orgId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization'],
    }),

    fetchDepartments: builder.query({
      query: (orgId) => `rbac/departments/?organization=${orgId}`,
      providesTags: ['Department'],
    }),

    createDepartment: builder.mutation({
      query: ({ orgId, ...deptData }) => ({
        url: `rbac/departments/`,
        method: 'POST',
        body: { ...deptData, organization: orgId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Department'],
    }),

    updateDepartment: builder.mutation({
      query: ({ deptId, ...deptData }) => ({
        url: `rbac/departments/${deptId}/`,
        method: 'PUT',
        body: deptData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Department'],
    }),

    deleteDepartment: builder.mutation({
      query: (deptId) => ({
        url: `rbac/departments/${deptId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Department'],
    }),

    // ========== AUDIT LOG MANAGEMENT ==========
    fetchAuditLogs: builder.query({
      query: (params = {}) => ({
        url: 'audit-logs/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 50,
          ...params,
        },
      }),
      providesTags: ['AuditLog'],
    }),

    fetchAuditLogDetails: builder.query({
      query: (logId) => `audit-logs/${logId}/`,
      providesTags: (result, error, id) => [{ type: 'AuditLog', id }],
    }),

    exportAuditLogs: builder.mutation({
      query: (params = {}) => ({
        url: 'audit-logs/export/',
        method: 'POST',
        body: params,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob',
      }),
    }),

    fetchSecurityEvents: builder.query({
      query: (params = {}) => ({
        url: 'security-events/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 50,
          ...params,
        },
      }),
      providesTags: ['SecurityEvent'],
    }),

    // ========== USER SESSION MANAGEMENT ==========
    fetchActiveSessions: builder.query({
      query: (params = {}) => ({
        url: 'active-sessions/',
        params,
      }),
      providesTags: ['UserSession'],
    }),

    terminateUserSession: builder.mutation({
      query: (sessionId) => ({
        url: `sessions/${sessionId}/terminate/`,
        method: 'POST',
      }),
      invalidatesTags: ['UserSession'],
    }),

    terminateAllUserSessions: builder.mutation({
      query: (userId) => ({
        url: `users/${userId}/terminate-sessions/`,
        method: 'POST',
      }),
      invalidatesTags: ['UserSession'],
    }),

    // ========== SYSTEM HEALTH & MONITORING ==========
    fetchSystemHealth: builder.query({
      query: () => 'system/health/',
      providesTags: ['SystemHealth'],
    }),

    fetchSystemMetrics: builder.query({
      query: (timeRange = '1h') => ({
        url: 'system/metrics/',
        params: { time_range: timeRange },
      }),
      providesTags: ['SystemHealth'],
    }),

    fetchDatabaseStats: builder.query({
      query: () => 'system/database-stats/',
      providesTags: ['SystemHealth'],
    }),

    fetchDashboardStatistics: builder.query({
      query: () => 'system/dashboard-stats/',
      providesTags: ['SystemHealth'],
    }),

    // ========== SYSTEM SETTINGS ==========
    fetchSystemSettings: builder.query({
      query: () => 'system/settings/',
      providesTags: ['SystemSettings'],
    }),

    updateSystemSettings: builder.mutation({
      query: (settings) => ({
        url: 'system/settings/',
        method: 'PUT',
        body: settings,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SystemSettings'],
    }),

    // ========== SECURITY MANAGEMENT ==========
    fetchFailedLoginAttempts: builder.query({
      query: (params = {}) => ({
        url: 'failed-logins/',
        params,
      }),
      providesTags: ['SecurityEvent'],
    }),

    blockIpAddress: builder.mutation({
      query: ({ ipAddress, reason, duration }) => ({
        url: 'security/block-ip/',
        method: 'POST',
        body: { ip_address: ipAddress, reason, duration },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SecurityEvent'],
    }),

    unblockIpAddress: builder.mutation({
      query: (ipAddress) => ({
        url: `security/unblock-ip/${ipAddress}/`,
        method: 'POST',
      }),
      invalidatesTags: ['SecurityEvent'],
    }),

    // ========== BACKUP & MAINTENANCE ==========
    initiateSystemBackup: builder.mutation({
      query: (backupType = 'full') => ({
        url: 'system/backup/',
        method: 'POST',
        body: { backup_type: backupType },
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    fetchBackupHistory: builder.query({
      query: () => 'system/backup/history/',
      providesTags: ['SystemHealth'],
    }),

    performSystemMaintenance: builder.mutation({
      query: (maintenanceTask) => ({
        url: 'system/maintenance/',
        method: 'POST',
        body: maintenanceTask,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
  }),
});

// Export hooks for Super Admin Portal components
export const {
  // RBAC User Management
  useFetchAllUsersQuery,
  useCreateSystemUserMutation,
  useUpdateSystemUserMutation,
  useDeleteSystemUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useResetUserPasswordMutation,

  // RBAC Group Management
  useFetchAllGroupsQuery,
  useCreateSystemGroupMutation,
  useUpdateSystemGroupMutation,
  useDeleteSystemGroupMutation,
  useAssignUserToGroupMutation,
  useRemoveUserFromGroupMutation,

  // RBAC Roles & Permissions
  useFetchAllRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useFetchAllPermissionsQuery,
  useAssignPermissionToRoleMutation,
  useRemovePermissionFromRoleMutation,

  // Organization & Department Management
  useFetchOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useFetchDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,

  // Audit Log Management
  useFetchAuditLogsQuery,
  useFetchAuditLogDetailsQuery,
  useExportAuditLogsMutation,
  useFetchSecurityEventsQuery,

  // User Session Management
  useFetchActiveSessionsQuery,
  useTerminateUserSessionMutation,
  useTerminateAllUserSessionsMutation,

  // System Health & Monitoring
  useFetchSystemHealthQuery,
  useFetchSystemMetricsQuery,
  useFetchDatabaseStatsQuery,
  useFetchDashboardStatisticsQuery,

  // System Settings
  useFetchSystemSettingsQuery,
  useUpdateSystemSettingsMutation,

  // Security Management
  useFetchFailedLoginAttemptsQuery,
  useBlockIpAddressMutation,
  useUnblockIpAddressMutation,

  // Backup & Maintenance
  useInitiateSystemBackupMutation,
  useFetchBackupHistoryQuery,
  usePerformSystemMaintenanceMutation,
} = superAdminApiSlice;
